import React from 'react';
import { CheckCircle, FileText, Shield, Award, Clock, TrendingUp } from 'lucide-react';
import ServiceFAQ from '../../components/ServiceFAQ';

const MergersAcquisitions = ({ onGetStarted }) => {
  const benefits = [
    'Strategic M&A planning and execution',
    'Comprehensive due diligence coordination',
    'Transaction structure optimization',
    'Regulatory compliance and approvals',
    'Negotiation and closing support',
    'Post-merger integration guidance',
  ];

  const process = [
    { title: 'Strategic Planning', description: 'Define objectives, identify targets, and develop transaction strategy.' },
    { title: 'Due Diligence', description: 'Conduct comprehensive financial, legal, and operational review.' },
    { title: 'Deal Structuring', description: 'Optimize transaction structure for tax and business objectives.' },
    { title: 'Closing & Integration', description: 'Execute transaction and support seamless integration.' },
  ];

  const dealTypes = [
    'Mergers',
    'Acquisitions',
    'Divestitures',
    'Spin-Offs',
    'Carve-Outs',
    'Tender Offers',
    'Recapitalizations',
    'Going Private Transactions',
  ];

  const whyChoose = [
    { icon: Award, title: 'Deal Expertise', description: 'Extensive M&A experience across industries and deal sizes.' },
    { icon: Shield, title: 'Risk Mitigation', description: 'Thorough due diligence to identify and address risks.' },
    { icon: Clock, title: 'Timely Execution', description: 'Efficient process management to meet closing deadlines.' },
    { icon: TrendingUp, title: 'Value Creation', description: 'Strategic guidance to maximize transaction value.' },
  ];

  const faqs = [
    { question: 'What is the difference between a merger and an acquisition?', answer: 'A merger combines two companies into a new entity, while an acquisition involves one company purchasing another. Mergers typically involve similar-sized companies, while acquisitions can involve companies of any size. The legal and tax implications differ significantly.' },
    { question: 'How long does an M&A transaction typically take?', answer: 'M&A transactions typically take several months to over a year, depending on complexity, regulatory requirements, and deal size. Simple acquisitions may close in months, while complex public company mergers can take a year or more due to regulatory approvals and shareholder votes.' },
    { question: 'What is due diligence in M&A transactions?', answer: 'Due diligence is the comprehensive investigation of the target company covering financial, legal, operational, and commercial matters. It includes reviewing contracts, financial statements, intellectual property, litigation, regulatory compliance, and material business relationships.' },
    { question: 'What are the main legal documents in an M&A transaction?', answer: 'Key documents include the letter of intent, purchase agreement, disclosure schedules, employment and non-compete agreements, escrow agreement, and various corporate resolutions. Public company deals also require proxy statements and regulatory filings.' },
    { question: 'How are M&A transactions structured?', answer: 'Common structures include stock purchases, asset purchases, mergers, and tender offers. Structure choice depends on tax considerations, liability concerns, required approvals, and business objectives. We help clients select the optimal structure for their situation.' },
  ];

  return (
    <div className="pt-20">
      <section 
        className="py-24 md:py-32 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1656646424292-cf207f3f1749?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-[#2C3E50]/80"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-3xl">
            <div className="text-sm uppercase tracking-[0.2em] font-medium text-white/90 mb-4">Business Law Services</div>
            <h1 className="text-5xl lg:text-6xl tracking-tight leading-none text-white font-bold mb-6">Mergers & Acquisitions</h1>
            <p className="text-xl text-white/90 leading-relaxed mb-8">Navigate complex M&A transactions with strategic legal guidance from experienced deal attorneys.</p>
            <button onClick={onGetStarted} className="bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-8 py-4 font-medium">Get Started</button>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-6">Overview</h2>
              <p className="text-base leading-relaxed text-[#2E2E2E]/80 mb-6">Our M&A team provides comprehensive transaction support including due diligence, deal structuring, negotiation, and post-closing integration. We represent both buyers and sellers in complex mergers and acquisitions.</p>
              <p className="text-base leading-relaxed text-[#2E2E2E]/80">With extensive experience across industries, we guide clients through every phase of the M&A process.</p>
              <h3 className="text-xl lg:text-2xl text-[#2C3E50] font-semibold mb-6 mt-8">Key Benefits</h3>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#2C3E50] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <span className="text-base text-[#2E2E2E]/80">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img src="https://images.unsplash.com/photo-1649211612005-6f7728fecbd0?w=800&q=80" alt="Mergers and acquisitions boardroom" className="w-full h-[450px] object-cover shadow-lg" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-4">Transaction Types</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80 max-w-2xl mx-auto">We handle all types of M&A transactions and corporate restructurings.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mobile-swiper">
            {dealTypes.map((type, index) => (
              <div key={index} className="bg-white border border-[#2C3E50]/5 shadow-sm p-6 hover:border-[#2C3E50]/20 transition-all">
                <FileText className="w-8 h-8 text-[#CA9A67] mb-3" strokeWidth={1.5} />
                <h4 className="font-semibold text-[#2C3E50]">{type}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 relative overflow-hidden" style={{ backgroundImage: 'url(https://images.pexels.com/photos/3760067/pexels-photo-3760067.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
        <div className="absolute inset-0 bg-[#2C3E50]/90"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-16">
            <div className="text-sm uppercase tracking-[0.2em] font-medium text-[#CA9A67] mb-4">Our Approach</div>
            <h2 className="text-3xl lg:text-4xl tracking-tight text-white font-bold mb-4">Our Process</h2>
            <p className="text-base leading-relaxed text-white/70 max-w-2xl mx-auto">A proven M&A process that delivers successful outcomes.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mobile-swiper">
            {process.map((step, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 p-6 hover:bg-white/10 transition-all duration-300">
                <div className="text-4xl font-bold text-[#CA9A67] mb-4">{index + 1}</div>
                <h3 className="text-xl text-white font-semibold mb-3">{step.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img src="https://images.pexels.com/photos/7433840/pexels-photo-7433840.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Why choose our M&A team" className="w-full h-[500px] object-cover shadow-lg" />
            </div>
            <div>
              <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-8">Why Choose Us</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mobile-swiper">
                {whyChoose.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="bg-white border border-[#2C3E50]/5 shadow-sm p-6">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-[#CA9A67] mb-4">
                        <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                      </div>
                      <h3 className="text-lg text-[#2C3E50] font-semibold mb-2">{item.title}</h3>
                      <p className="text-sm text-[#2E2E2E]/70 leading-relaxed">{item.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <ServiceFAQ faqs={faqs} />

      <section 
        className="py-24 md:py-32 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/3184357/pexels-photo-3184357.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-[#2C3E50]/85"></div>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="text-3xl lg:text-4xl tracking-tight text-white font-bold mb-6">Planning an M&A Transaction?</h2>
          <p className="text-xl text-white/90 leading-relaxed mb-8">Contact us for expert M&A legal advisory services.</p>
          <button onClick={onGetStarted} className="bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-8 py-4 font-medium">Get Legal Help Now</button>
        </div>
      </section>
    </div>
  );
};

export default MergersAcquisitions;
